import { supabase } from '@/lib/supabase';

/**
 * ---------------------------------------------------------------------------
 * LEAD CREDITS — single source of truth.
 *
 * 1 CREDIT = ACCESS TO 1 LEAD. Every unlock/claim deducts exactly one credit.
 *
 * Where credits come from:
 *   Plan credits    Basic $99/mo -> 5, Pro $199/mo -> 10, Premium $499/mo -> 20
 *                   (numbers live in mkt_plans.included_leads, never hardcoded).
 *                   They EXPIRE at the end of the billing cycle: unused credits
 *                   are FORFEITED and do NOT roll over. A FRESH ALLOTMENT IS
 *                   GRANTED ONLY BY A VERIFIED SETTLED PAYMENT (payment-webhook).
 *                   A renewal date arriving grants nothing on its own.
 *   Extra credits   A one-off $29 purchase grants exactly 1 credit
 *                   (mkt_profiles.extra_credits). A separate balance: it is NOT
 *                   touched by membership cycle expiry. Spent only after the
 *                   plan allowance for the period is used up.
 *
 * ENFORCEMENT IS SERVER-SIDE — the browser is never trusted:
 *   mkt_claim_lead(listing)   -> ONE transaction: re-derives the buyer from the
 *                                JWT, row-locks the listing, re-counts the
 *                                contractors on it, refuses at capacity
 *                                (6 of 6 = sold out), deducts one credit under a
 *                                row lock and inserts the purchase with a unique
 *                                (listing, buyer) index. Double clicks, two tabs
 *                                and crafted REST calls cannot double-spend a
 *                                credit, claim a 7th slot or self-grant access.
 *   mkt_reset_lead_cycles()   -> EXPIRE ONLY. Forfeits the unused balance
 *                                (plan_expired), zeroes the spendable balance and
 *                                parks the subscription in awaiting_payment. It
 *                                never writes plan_grant, never restores included
 *                                leads, never touches payments_made or extra
 *                                credits and never extends the paid period.
 *   payment-webhook           -> THE ONLY membership grant path. A settled
 *                                payment tops the balance up to the plan
 *                                allowance and reactivates the subscription.
 *   mkt_sweep_lead_capacity() -> hourly reconciliation + sold-out admin alerts.
 * Everything below is display logic on top of those numbers.
 * ---------------------------------------------------------------------------
 */


export interface LeadBalance {
  subscriptionId: string | null;
  planCode: string;
  status: string;
  included: number;
  used: number;
  /** Credits left in THIS billing cycle (expire at renewal). */
  remaining: number;
  /** Individually purchased $29 credits. Never expire with the cycle. */
  extra: number;
  /** remaining + extra — what the contractor can actually spend right now. */
  spendable: number;
  periodEnd: string | null;
  /** true when the plan allowance for this period is fully consumed */
  exhausted: boolean;
  hasSubscription: boolean;
}

export const EMPTY_BALANCE: LeadBalance = {
  subscriptionId: null,
  planCode: '',
  status: 'none',
  included: 0,
  used: 0,
  remaining: 0,
  extra: 0,
  spendable: 0,
  periodEnd: null,
  exhausted: false,
  hasSubscription: false,
};

/** Read the current billing period's credit counters for a user. */
export const getLeadBalance = async (userId?: string): Promise<LeadBalance> => {
  if (!userId) return EMPTY_BALANCE;

  const [subRes, profRes] = await Promise.all([
    supabase
      .from('mkt_subscriptions')
      .select('id, plan_code, status, included_leads, leads_used, current_period_end')
      .eq('user_id', userId)
      .in('status', ['active', 'past_due'])
      .order('created_at', { ascending: false })
      .limit(1),
    supabase.from('mkt_profiles').select('extra_credits').eq('id', userId).maybeSingle(),
  ]);

  const extra = Math.max(0, profRes.data?.extra_credits || 0);
  const row = subRes.data && subRes.data[0];

  if (!row) return { ...EMPTY_BALANCE, extra, spendable: extra };

  const included = row.included_leads || 0;
  const used = row.leads_used || 0;
  const remaining = Math.max(0, included - used);
  return {
    subscriptionId: row.id,
    planCode: row.plan_code || '',
    status: row.status,
    included,
    used,
    remaining,
    extra,
    spendable: remaining + extra,
    periodEnd: row.current_period_end || null,
    exhausted: included > 0 && remaining === 0,
    hasSubscription: true,
  };
};


/**
 * NOTE — there is deliberately NO client-side "start a paid period" helper here.
 * Membership credits are granted in exactly one place: the payment-webhook edge
 * function, after a verified settled payment. A previous startSubscriptionPeriod()
 * helper (insert subscription + set profile credits) was removed because it was a
 * second, unverified grant path waiting to be called. Nothing in the browser may
 * create an allowance.
 */


/** Human readable renewal date, localized. */
export const formatPeriodEnd = (iso: string | null, lang: string) => {
  if (!iso) return '—';
  try {
    return new Date(iso).toLocaleDateString(lang === 'es' ? 'es-US' : 'en-US', {
      month: 'short',
      day: 'numeric',
      year: 'numeric',
    });
  } catch {
    return iso.slice(0, 10);
  }
};
